import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Tabs,
  Tab,
  Alert,
  Tooltip,
  Badge,
  LinearProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  Container,
  Stack,
  Grid,
  Avatar,
  InputAdornment,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Inventory as InventoryIcon,
  Warning as WarningIcon,
  TrendingUp as TrendingUpIcon,
  Assignment as AssignmentIcon,
  ShoppingCart as ShoppingCartIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Build as BuildIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  Visibility as ViewIcon,
  MoreVert as MoreVertIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { equipmentService } from '../services/api';
import { useAppDispatch, useAppSelector } from '../store';
import { fetchVehicles } from '../store/vehiclesSlice';
import LoadingSpinner from '../components/LoadingSpinner';
import type { 
  Equipment as EquipmentType, 
  EquipmentUsage, 
  Vehicle, 
  EquipmentFormData,
  EquipmentUsageFormData,
  EquipmentStats 
} from '../types';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`equipment-tabpanel-${index}`}
      aria-labelledby={`equipment-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const Equipment: React.FC = () => {
  const dispatch = useAppDispatch();
  const vehiclesFromStore = useAppSelector((s) => s.vehicles.vehicles);
  const [tabValue, setTabValue] = useState(0);
  const [equipment, setEquipment] = useState<EquipmentType[]>([]);
  const [lowStockItems, setLowStockItems] = useState<EquipmentType[]>([]);
  const [usage, setUsage] = useState<EquipmentUsage[]>([]);
  const [usagePagination, setUsagePagination] = useState<{
    total: number;
    currentPage: number;
    lastPage: number;
    perPage: number;
  } | null>(null);
  const [usageLoadingMore, setUsageLoadingMore] = useState(false);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [stats, setStats] = useState<EquipmentStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // Dialog states
  const [openDialog, setOpenDialog] = useState(false);
  const [openUsageDialog, setOpenUsageDialog] = useState(false);
  const [editingEquipment, setEditingEquipment] = useState<EquipmentType | null>(null);
  const [formData, setFormData] = useState<EquipmentFormData>({
    name: '',
    category: '',
    quantity_total: 0,
    quantity_available: 0,
    unit_cost: 0,
    supplier: '',
    location: '',
    minimum_stock_level: 0,
  });
  const [usageFormData, setUsageFormData] = useState<EquipmentUsageFormData>({
    equipment_id: 0,
    vehicle_id: 0,
    quantity_used: 1,
    usage_date: format(new Date(), 'yyyy-MM-dd'),
    purpose: '',
  });

  useEffect(() => {
    loadData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity });
  };

  const loadData = async () => {
    try {
      setLoading(true);
      setDetailsLoading(true);
      setError(null);

      const equipmentPromise = equipmentService.getAll();

      const vehiclesPromise = (async (): Promise<Vehicle[]> => {
        if (vehiclesFromStore.length > 0) {
          return vehiclesFromStore;
        }
        try {
          const paginated = await dispatch(fetchVehicles({})).unwrap();
          return paginated.data ?? [];
        } catch {
          return [];
        }
      })();

      const [equipmentRes, vehicleList] = await Promise.all([equipmentPromise, vehiclesPromise]);

      const equipmentData = equipmentRes.data.data;
      setEquipment(equipmentData);
      setVehicles(vehicleList);
      setLoading(false);

      const [lowStockRes, usageRes, statsRes] = await Promise.all([
        equipmentService.getLowStock().catch(() => null),
        equipmentService.getUsage({ per_page: 100, page: 1 }).catch(() => null),
        equipmentService.getStats().catch(() => null),
      ]);

      if (lowStockRes) {
        setLowStockItems(lowStockRes.data.data);
      } else {
        const lowStock = equipmentData.filter(
          (item: EquipmentType) => item.quantity_available <= item.minimum_stock_level
        );
        setLowStockItems(lowStock);
      }

      if (usageRes?.data && Array.isArray(usageRes.data.data)) {
        const p = usageRes.data as {
          data: EquipmentUsage[];
          total?: number;
          current_page?: number;
          last_page?: number;
          per_page?: number;
        };
        setUsage(p.data);
        setUsagePagination({
          total: p.total ?? p.data.length,
          currentPage: p.current_page ?? 1,
          lastPage: p.last_page ?? 1,
          perPage: p.per_page ?? 100,
        });
      } else {
        setUsage([]);
        setUsagePagination(null);
      }

      if (statsRes) {
        setStats(statsRes.data.data);
      } else {
        const lowStockCount = equipmentData.filter(
          (item: EquipmentType) => item.quantity_available <= item.minimum_stock_level
        ).length;
        const inUseCount = equipmentData.filter(
          (item: EquipmentType) => item.quantity_total > item.quantity_available
        ).length;
        const totalValue = equipmentData.reduce(
          (sum: number, item: EquipmentType) => sum + item.quantity_total * (item.unit_cost || 0),
          0
        );

        const calculatedStats: EquipmentStats = {
          total_items: equipmentData.length,
          total_categories: new Set(equipmentData.map((item) => item.category)).size,
          low_stock_items: lowStockCount,
          out_of_stock_items: equipmentData.filter((item) => item.quantity_available === 0).length,
          total_value: totalValue,
          items_in_use: inUseCount,
          total_usage_records: 0,
          categories: [],
        };
        setStats(calculatedStats);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erreur lors du chargement des données';
      setError(errorMessage);
      showSnackbar('Erreur lors du chargement des données', 'error');
    } finally {
      setDetailsLoading(false);
      setLoading(false);
    }
  };

  const loadMoreUsage = async () => {
    if (!usagePagination || usagePagination.currentPage >= usagePagination.lastPage || usageLoadingMore) {
      return;
    }
    setUsageLoadingMore(true);
    try {
      const nextPage = usagePagination.currentPage + 1;
      const res = await equipmentService.getUsage({
        per_page: usagePagination.perPage,
        page: nextPage,
      });
      const body = res.data as {
        data: EquipmentUsage[];
        total?: number;
        current_page?: number;
        last_page?: number;
        per_page?: number;
      };
      if (!Array.isArray(body.data)) {
        return;
      }
      setUsage((prev) => [...prev, ...body.data]);
      setUsagePagination({
        total: body.total ?? usagePagination.total,
        currentPage: body.current_page ?? nextPage,
        lastPage: body.last_page ?? usagePagination.lastPage,
        perPage: body.per_page ?? usagePagination.perPage,
      });
    } catch {
      showSnackbar('Impossible de charger la suite de l\'historique', 'error');
    } finally {
      setUsageLoadingMore(false);
    }
  };

  const handleSubmit = async () => {
    try {
      if (editingEquipment) {
        await equipmentService.update(editingEquipment.id, formData);
        showSnackbar('Équipement mis à jour avec succès', 'success');
      } else {
        await equipmentService.create(formData);
        showSnackbar('Équipement créé avec succès', 'success');
      }
      handleCloseDialog();
      loadData();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erreur lors de la sauvegarde';
      showSnackbar(errorMessage, 'error');
    }
  };

  const handleUsageSubmit = async () => {
    try {
      await equipmentService.recordUsage(usageFormData);
      showSnackbar('Utilisation enregistrée avec succès', 'success');
      handleCloseUsageDialog();
      loadData();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erreur lors de l\'enregistrement de l\'utilisation';
      showSnackbar(errorMessage, 'error');
    }
  };

  const handleEdit = (item: EquipmentType) => {
    setEditingEquipment(item);
    setFormData({
      name: item.name,
      category: item.category || '',
      quantity_total: item.quantity_total,
      quantity_available: item.quantity_available,
      unit_cost: item.unit_cost || 0,
      supplier: item.supplier || '',
      location: item.location || '',
      minimum_stock_level: item.minimum_stock_level,
    });
    setOpenDialog(true);
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cet équipement ?')) {
      try {
        await equipmentService.delete(id);
        showSnackbar('Équipement supprimé avec succès', 'success');
        loadData();
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Erreur lors de la suppression';
        showSnackbar(errorMessage, 'error');
      }
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingEquipment(null);
    setFormData({
      name: '',
      category: '',
      quantity_total: 0,
      quantity_available: 0,
      unit_cost: 0,
      supplier: '',
      location: '',
      minimum_stock_level: 0,
    });
  };

  const handleCloseUsageDialog = () => {
    setOpenUsageDialog(false);
    setUsageFormData({
      equipment_id: 0,
      vehicle_id: 0,
      quantity_used: 1,
      usage_date: format(new Date(), 'yyyy-MM-dd'),
      purpose: '',
    });
  };

  const getStockLevelColor = (available: number, minimum: number): 'error' | 'warning' | 'success' => {
    if (available === 0) return 'error';
    if (available <= minimum) return 'warning';
    return 'success';
  };

  const getStockPercentage = (available: number, total: number): number => {
    return total > 0 ? (available / total) * 100 : 0;
  };

  if (loading) {
    return <LoadingSpinner message="Chargement des équipements..." variant="fullscreen" />;
  }

  return (
    <>
      <Box sx={{ 
        minHeight: '100vh',
        width: '100%',
        display: 'flex',
        flexDirection: 'column'
      }}>
        <Container maxWidth="xl" sx={{ py: 4, flex: 1 }}>
        {detailsLoading && !loading && (
          <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />
        )}
        {/* Header Section */}
        <Box sx={{ mb: 4 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2 }}>
            <Box>
              <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                Suivi et gestion du stock d'équipements
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {equipment.length} équipements • {lowStockItems.length} en stock faible
              </Typography>
            </Box>
            <Stack direction="row" spacing={2}>
              <Tooltip title="Actualiser les données">
                <span>
                <IconButton 
                  onClick={loadData}
                  disabled={loading || detailsLoading}
                  aria-label="Actualiser les données"
                  sx={{ 
                    bgcolor: 'primary.main', 
                    color: 'white',
                    '&:hover': { bgcolor: 'primary.dark' },
                    '&:disabled': { bgcolor: 'grey.300' }
                  }}
                >
                  <RefreshIcon />
                </IconButton>
                </span>
              </Tooltip>
              <Button
                variant="outlined"
                startIcon={<AssignmentIcon />}
                onClick={() => setOpenUsageDialog(true)}
                sx={{
                  borderRadius: 2,
                  borderColor: 'primary.main',
                  color: 'primary.main',
                  '&:hover': {
                    borderColor: 'primary.dark',
                    bgcolor: 'primary.50',
                    transform: 'translateY(-2px)',
                  },
                  transition: 'all 0.3s ease-in-out',
                }}
              >
                Enregistrer utilisation
              </Button>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setOpenDialog(true)}
                sx={{
                  borderRadius: 2,
                  background: 'linear-gradient(135deg, #4caf50 0%, #45a049 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #45a049 0%, #3d8b40 100%)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 6px 20px rgba(76, 175, 80, 0.3)',
                  },
                  transition: 'all 0.3s ease-in-out',
                }}
              >
                Nouvel équipement
              </Button>
            </Stack>
          </Stack>
          {error && (
            <Alert 
              severity="error" 
              sx={{ mb: 3 }} 
              onClose={() => setError(null)}
              action={
                <Button color="inherit" size="small" onClick={loadData}>
                  Réessayer
                </Button>
              }
            >
              {error}
            </Alert>
          )}
        </Box>

        {/* Statistics Cards */}
        {stats && (
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
              <Card 
                sx={{ 
                  height: '100%',
                  background: 'linear-gradient(135deg, #2196f3 0%, #1976d2 100%)',
                  color: 'white',
                  transition: 'all 0.3s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 8px 25px rgba(33, 150, 243, 0.3)',
                  }
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography variant="h6" sx={{ opacity: 0.9, mb: 1 }}>
                        Total Articles
                      </Typography>
                      <Typography variant="h3" fontWeight="bold">
                        {stats.total_items}
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.8, mt: 1 }}>
                        Équipements en stock
                      </Typography>
                    </Box>
                    <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 56, height: 56 }}>
                      <InventoryIcon sx={{ fontSize: 28 }} />
                    </Avatar>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
              <Card 
                sx={{ 
                  height: '100%',
                  background: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)',
                  color: 'white',
                  transition: 'all 0.3s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 8px 25px rgba(255, 152, 0, 0.3)',
                  }
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography variant="h6" sx={{ opacity: 0.9, mb: 1 }}>
                        Stock Faible
                      </Typography>
                      <Typography variant="h3" fontWeight="bold">
                        {stats.low_stock_items}
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.8, mt: 1 }}>
                        Nécessitent réapprovisionnement
                      </Typography>
                    </Box>
                    <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 56, height: 56 }}>
                      <WarningIcon sx={{ fontSize: 28 }} />
                    </Avatar>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
              <Card 
                sx={{ 
                  height: '100%',
                  background: 'linear-gradient(135deg, #9c27b0 0%, #7b1fa2 100%)',
                  color: 'white',
                  transition: 'all 0.3s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 8px 25px rgba(156, 39, 176, 0.3)',
                  }
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography variant="h6" sx={{ opacity: 0.9, mb: 1 }}>
                        En Utilisation
                      </Typography>
                      <Typography variant="h3" fontWeight="bold">
                        {stats.items_in_use}
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.8, mt: 1 }}>
                        Unités en prêt (enregistrements « en cours »)
                      </Typography>
                    </Box>
                    <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 56, height: 56 }}>
                      <TrendingUpIcon sx={{ fontSize: 28 }} />
                    </Avatar>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
              <Card 
                sx={{ 
                  height: '100%',
                  background: 'linear-gradient(135deg, #4caf50 0%, #388e3c 100%)',
                  color: 'white',
                  transition: 'all 0.3s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 8px 25px rgba(76, 175, 80, 0.3)',
                  }
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography variant="h6" sx={{ opacity: 0.9, mb: 1 }}>
                        Valeur Totale
                      </Typography>
                      <Typography variant="h3" fontWeight="bold">
                        {stats.total_value ? `${Math.round(stats.total_value)}€` : '0€'}
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.8, mt: 1 }}>
                        Valeur du stock
                      </Typography>
                    </Box>
                    <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 56, height: 56 }}>
                      <ShoppingCartIcon sx={{ fontSize: 28 }} />
                    </Avatar>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {/* Tabs */}
        <Card sx={{ mb: 3, borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
          <Tabs 
            value={tabValue} 
            onChange={(_, newValue) => setTabValue(newValue)}
            sx={{
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '1rem',
                minHeight: 48,
                '&.Mui-selected': {
                  color: 'primary.main',
                },
              },
              '& .MuiTabs-indicator': {
                height: 3,
                borderRadius: '3px 3px 0 0',
                background: 'linear-gradient(135deg, #4caf50 0%, #45a049 100%)',
              },
            }}
          >
            <Tab 
              label={
                <Stack direction="row" alignItems="center" spacing={1}>
                  <InventoryIcon fontSize="small" />
                  <span>Tous les équipements</span>
                </Stack>
              } 
            />
            <Tab 
              label={
                <Stack direction="row" alignItems="center" spacing={1}>
                  <WarningIcon fontSize="small" />
                  <span>Stock faible</span>
                  <Badge badgeContent={lowStockItems.length} color="warning" />
                </Stack>
              } 
            />
            <Tab 
              label={
                <Stack direction="row" alignItems="center" spacing={1}>
                  <AssignmentIcon fontSize="small" />
                  <span>Historique d'utilisation</span>
                </Stack>
              } 
            />
          </Tabs>
        </Card>

        {/* All Equipment */}
        <TabPanel value={tabValue} index={0}>
          <Card sx={{ borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
            <TableContainer>
              <Table sx={{ tableLayout: 'fixed', width: '100%' }}>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell sx={{ fontWeight: 'bold', py: 2, width: '200px' }}>Nom</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', py: 2, width: '120px' }}>Catégorie</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', py: 2, width: '150px' }}>Stock</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', py: 2, width: '100px' }}>Niveau</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', py: 2, width: '150px' }}>Emplacement</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', py: 2, width: '120px' }}>Coût unitaire</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold', py: 2, width: '120px' }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {equipment.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                        <Stack alignItems="center" spacing={2}>
                          <InventoryIcon sx={{ fontSize: 64, color: 'grey.400' }} />
                          <Typography variant="h6" color="text.secondary">
                            Aucun équipement trouvé
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Commencez par ajouter votre premier équipement
                          </Typography>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ) : (
                    equipment.map((item) => {
                      const stockPercentage = getStockPercentage(item.quantity_available, item.quantity_total);
                      const stockColor = getStockLevelColor(item.quantity_available, item.minimum_stock_level);
                      return (
                        <TableRow 
                          key={item.id}
                          sx={{ 
                            '&:hover': { bgcolor: 'grey.50' },
                            transition: 'background-color 0.2s ease-in-out'
                          }}
                        >
                          <TableCell>
                            <Box sx={{ overflow: 'hidden' }}>
                              <Typography 
                                variant="subtitle2" 
                                fontWeight="bold"
                                sx={{
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap'
                                }}
                              >
                                {item.name}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip 
                              size="small" 
                              label={item.category}
                              sx={{ 
                                borderRadius: 2,
                                fontWeight: 500
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Box>
                              <Typography variant="body2" fontWeight="medium">
                                {item.quantity_available} / {item.quantity_total}
                              </Typography>
                              <LinearProgress 
                                variant="determinate" 
                                value={stockPercentage} 
                                color={stockColor}
                                sx={{ 
                                  mt: 1, 
                                  height: 6, 
                                  borderRadius: 3,
                                  bgcolor: 'grey.200'
                                }}
                              />
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip 
                              size="small" 
                              color={stockColor}
                              label={
                                item.quantity_available === 0 ? 'Rupture' :
                                item.quantity_available <= item.minimum_stock_level ? 'Faible' : 'OK'
                              }
                              sx={{ 
                                borderRadius: 2,
                                fontWeight: 500
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Box sx={{ overflow: 'hidden' }}>
                              <Typography 
                                variant="body2"
                                sx={{
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap'
                                }}
                              >
                                {item.location || 'N/A'}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight="medium">
                              {item.unit_cost ? `${item.unit_cost}€` : 'N/A'}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Stack direction="row" spacing={1} justifyContent="center">
                              <Tooltip title="Modifier">
                                <IconButton 
                                  size="small" 
                                  onClick={() => handleEdit(item)}
                                  sx={{
                                    color: 'primary.main',
                                    '&:hover': {
                                      bgcolor: 'primary.50',
                                      transform: 'scale(1.1)'
                                    },
                                    transition: 'all 0.2s ease-in-out'
                                  }}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Supprimer">
                                <IconButton 
                                  size="small" 
                                  onClick={() => handleDelete(item.id)}
                                  sx={{
                                    color: 'error.main',
                                    '&:hover': {
                                      bgcolor: 'error.50',
                                      transform: 'scale(1.1)'
                                    },
                                    transition: 'all 0.2s ease-in-out'
                                  }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        </TabPanel>

        {/* Low Stock Items */}
        <TabPanel value={tabValue} index={1}>
          <Alert 
            severity="warning" 
            sx={{ 
              mb: 3,
              borderRadius: 2,
              '& .MuiAlert-message': {
                width: '100%'
              }
            }}
          >
            <Typography variant="subtitle1" fontWeight="bold">
              Attention - Stock Faible
            </Typography>
            <Typography variant="body2">
              Ces équipements ont un niveau de stock faible et nécessitent un réapprovisionnement urgent.
            </Typography>
          </Alert>
          <Card sx={{ borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
            <TableContainer>
              <Table sx={{ tableLayout: 'fixed', width: '100%' }}>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell sx={{ fontWeight: 'bold', py: 2, width: '250px' }}>Nom</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', py: 2, width: '120px' }}>Stock actuel</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', py: 2, width: '120px' }}>Stock minimum</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', py: 2, width: '100px' }}>Manque</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', py: 2, width: '150px' }}>Fournisseur</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', py: 2, width: '120px' }}>Coût estimé</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {lowStockItems.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 8 }}>
                        <Stack alignItems="center" spacing={2}>
                          <CheckCircleOutlineIcon sx={{ fontSize: 64, color: 'success.main' }} />
                          <Typography variant="h6" color="text.secondary">
                            Excellent ! Aucun stock faible
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Tous vos équipements ont un niveau de stock suffisant
                          </Typography>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ) : (
                    lowStockItems.map((item) => {
                      const shortage = item.minimum_stock_level - item.quantity_available;
                      const estimatedCost = shortage * (item.unit_cost || 0);
                      return (
                        <TableRow 
                          key={item.id}
                          sx={{ 
                            '&:hover': { bgcolor: 'warning.50' },
                            transition: 'background-color 0.2s ease-in-out'
                          }}
                        >
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
                              <WarningIcon sx={{ mr: 1, color: 'warning.main', fontSize: 20, flexShrink: 0 }} />
                              <Typography 
                                variant="subtitle2" 
                                fontWeight="bold"
                                sx={{
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap'
                                }}
                              >
                                {item.name}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip 
                              size="small" 
                              color="error" 
                              label={item.quantity_available}
                              sx={{ 
                                borderRadius: 2,
                                fontWeight: 500
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight="medium">
                              {item.minimum_stock_level}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography color="error.main" fontWeight="bold" variant="subtitle2">
                              {shortage}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ overflow: 'hidden' }}>
                              <Typography 
                                variant="body2"
                                sx={{
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap'
                                }}
                              >
                                {item.supplier || 'N/A'}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight="bold" color="error.main">
                              {estimatedCost > 0 ? `${estimatedCost.toFixed(2)}€` : 'N/A'}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        </TabPanel>

        {/* Usage History */}
        <TabPanel value={tabValue} index={2}>
          <Card sx={{ borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
            <TableContainer>
              <Table sx={{ tableLayout: 'fixed', width: '100%' }}>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell sx={{ fontWeight: 'bold', py: 2, width: '180px' }}>Équipement</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', py: 2, width: '150px' }}>Véhicule</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', py: 2, width: '120px' }}>Utilisateur</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', py: 2, width: '100px' }}>Quantité</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', py: 2, width: '120px' }}>Date d'utilisation</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', py: 2, width: '150px' }}>Objectif</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', py: 2, width: '100px' }}>Statut</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {usage.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                        <Stack alignItems="center" spacing={2}>
                          <AssignmentIcon sx={{ fontSize: 64, color: 'grey.400' }} />
                          <Typography variant="h6" color="text.secondary">
                            Aucun historique d'utilisation
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Les enregistrements d'utilisation apparaîtront ici
                          </Typography>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ) : (
                    usage.map((record) => (
                      <TableRow 
                        key={record.id}
                        sx={{ 
                          '&:hover': { bgcolor: 'grey.50' },
                          transition: 'background-color 0.2s ease-in-out'
                        }}
                      >
                        <TableCell>
                          <Box sx={{ overflow: 'hidden' }}>
                            <Typography 
                              variant="subtitle2" 
                              fontWeight="bold"
                              sx={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}
                            >
                              {record.equipment?.name || 'N/A'}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ overflow: 'hidden' }}>
                            <Typography 
                              variant="body2"
                              sx={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}
                            >
                              {record.vehicle ? 
                                `${record.vehicle.marque} ${record.vehicle.modele}` : 
                                'N/A'
                              }
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {record.user?.name || 'N/A'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="bold">
                            {record.quantity_used}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {format(new Date(record.usage_date), 'dd/MM/yyyy')}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ overflow: 'hidden' }}>
                            <Typography 
                              variant="body2"
                              sx={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}
                            >
                              {record.purpose || 'N/A'}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip 
                            size="small" 
                            color={record.status === 'returned' ? 'success' : 'warning'}
                            label={record.status === 'returned' ? 'Retourné' : 'En cours'}
                            sx={{ 
                              borderRadius: 2,
                              fontWeight: 500
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            {usagePagination && usage.length > 0 && (
              <Box sx={{ px: 2, py: 2, borderTop: 1, borderColor: 'divider' }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    Affichés : {usage.length} sur {usagePagination.total}
                  </Typography>
                  {usagePagination.currentPage < usagePagination.lastPage && (
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={loadMoreUsage}
                      disabled={usageLoadingMore}
                    >
                      {usageLoadingMore ? 'Chargement…' : 'Charger la suite'}
                    </Button>
                  )}
                </Stack>
              </Box>
            )}
          </Card>
        </TabPanel>
        </Container>
      </Box>

    {/* Create/Edit Equipment Dialog */}
    <Dialog 
      open={openDialog} 
      onClose={handleCloseDialog} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
        }
      }}
    >
      <DialogTitle sx={{ 
        pb: 2,
        background: 'linear-gradient(135deg, #4caf50 0%, #45a049 100%)',
        color: 'white',
        fontWeight: 'bold'
      }}>
        {editingEquipment ? 'Modifier l\'équipement' : 'Nouvel équipement'}
      </DialogTitle>
      <DialogContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
            <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', md: 'row' } }}>
              <TextField
                fullWidth
                label="Nom"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
              <TextField
                fullWidth
                label="Catégorie"
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                required
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', md: 'row' } }}>
              <TextField
                fullWidth
                label="Quantité totale"
                type="number"
                value={formData.quantity_total}
                onChange={(e) => setFormData({ ...formData, quantity_total: Number(e.target.value) })}
                required
                inputProps={{ min: 0 }}
              />
              <TextField
                fullWidth
                label="Quantité disponible"
                type="number"
                value={formData.quantity_available}
                onChange={(e) => setFormData({ ...formData, quantity_available: Number(e.target.value) })}
                required
                inputProps={{ min: 0, max: formData.quantity_total }}
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', md: 'row' } }}>
              <TextField
                fullWidth
                label="Stock minimum"
                type="number"
                value={formData.minimum_stock_level}
                onChange={(e) => setFormData({ ...formData, minimum_stock_level: Number(e.target.value) })}
                required
                inputProps={{ min: 0 }}
              />
              <TextField
                fullWidth
                label="Coût unitaire (€)"
                type="number"
                value={formData.unit_cost}
                onChange={(e) => setFormData({ ...formData, unit_cost: Number(e.target.value) })}
                inputProps={{ min: 0, step: 0.01 }}
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', md: 'row' } }}>
              <TextField
                fullWidth
                label="Fournisseur"
                value={formData.supplier}
                onChange={(e) => setFormData({ ...formData, supplier: e.target.value })}
              />
              <TextField
                fullWidth
                label="Emplacement"
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
              />
            </Box>
          </Box>
        </DialogContent>
      <DialogActions sx={{ p: 3, gap: 2 }}>
        <Button 
          onClick={handleCloseDialog}
          sx={{
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600
          }}
        >
          Annuler
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained"
          sx={{
            borderRadius: 2,
            background: 'linear-gradient(135deg, #4caf50 0%, #45a049 100%)',
            '&:hover': {
              background: 'linear-gradient(135deg, #45a049 0%, #3d8b40 100%)',
              transform: 'translateY(-2px)',
              boxShadow: '0 6px 20px rgba(76, 175, 80, 0.3)',
            },
            transition: 'all 0.3s ease-in-out',
            textTransform: 'none',
            fontWeight: 600
          }}
        >
          {editingEquipment ? 'Modifier' : 'Créer'}
        </Button>
      </DialogActions>
      </Dialog>

    {/* Usage Dialog */}
    <Dialog 
      open={openUsageDialog} 
      onClose={handleCloseUsageDialog} 
      maxWidth="sm" 
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
        }
      }}
    >
      <DialogTitle sx={{ 
        pb: 2,
        background: 'linear-gradient(135deg, #2196f3 0%, #1976d2 100%)',
        color: 'white',
        fontWeight: 'bold'
      }}>
        Enregistrer une utilisation
      </DialogTitle>
      <DialogContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
            <FormControl fullWidth required>
              <InputLabel>Équipement</InputLabel>
              <Select
                value={usageFormData.equipment_id}
                onChange={(e) => setUsageFormData({ ...usageFormData, equipment_id: Number(e.target.value) })}
              >
                {equipment.map((item) => (
                  <MenuItem key={item.id} value={item.id}>
                    {item.name} (Stock: {item.quantity_available})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Véhicule (optionnel)</InputLabel>
              <Select
                value={usageFormData.vehicle_id}
                onChange={(e) => setUsageFormData({ ...usageFormData, vehicle_id: Number(e.target.value) })}
              >
                <MenuItem value={0}>Aucun véhicule</MenuItem>
                {vehicles.map((vehicle) => (
                  <MenuItem key={vehicle.id} value={vehicle.id}>
                    {vehicle.marque} {vehicle.modele} ({vehicle.registration_number})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', md: 'row' } }}>
              <TextField
                fullWidth
                label="Quantité utilisée"
                type="number"
                value={usageFormData.quantity_used}
                onChange={(e) => setUsageFormData({ ...usageFormData, quantity_used: Number(e.target.value) })}
                required
                inputProps={{ min: 1 }}
              />
              <TextField
                fullWidth
                label="Date d'utilisation"
                type="date"
                value={usageFormData.usage_date}
                onChange={(e) => setUsageFormData({ ...usageFormData, usage_date: e.target.value })}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Box>
            <TextField
              fullWidth
              label="Objectif"
              value={usageFormData.purpose}
              onChange={(e) => setUsageFormData({ ...usageFormData, purpose: e.target.value })}
            />
          </Box>
        </DialogContent>
      <DialogActions sx={{ p: 3, gap: 2 }}>
        <Button 
          onClick={handleCloseUsageDialog}
          sx={{
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600
          }}
        >
          Annuler
        </Button>
        <Button 
          onClick={handleUsageSubmit} 
          variant="contained"
          sx={{
            borderRadius: 2,
            background: 'linear-gradient(135deg, #2196f3 0%, #1976d2 100%)',
            '&:hover': {
              background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
              transform: 'translateY(-2px)',
              boxShadow: '0 6px 20px rgba(33, 150, 243, 0.3)',
            },
            transition: 'all 0.3s ease-in-out',
            textTransform: 'none',
            fontWeight: 600
          }}
        >
          Enregistrer
        </Button>
      </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default Equipment;
